import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Download, ExternalLink, FolderOpen, Loader2, RefreshCw, Search } from "lucide-react";
import { connectParityApi, type ConversationFilesPage, type FilesScope } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { fileKind, formatBytes } from "@/lib/drive";
import { splitFileMarkers, type FileRef } from "@/lib/fileMarkers";
import { DriveFileRow, FileTypeIcon } from "./DriveFileCard";

type Entry =
    | { kind: "drive"; key: string; file: FileRef; sharedBy: string; sharedAt: string }
    | { kind: "legacy"; key: string; item: ConversationFilesPage["legacy"][number]; sharedBy: string; sharedAt: string };

/** Rows rendered before "Show more" — each Drive row resolves its item lazily. */
const PAGE = 50;

/**
 * Teams' Files tab for a 1:1 chat, group chat or channel: every file shared
 * in it, newest first, with who shared it, when and how big, and Open /
 * Download / Open in Drive. Drive files come from the messages' file markers
 * (the BFF pages through history and returns just the markers); each one is
 * resolved against Drive with your own access, so a file that was deleted or
 * unshared shows as unavailable. A channel's old Connect-stored attachments
 * are listed too, with Download.
 */
export function ConversationFiles({ scope, id, label }: { scope: FilesScope; id: string; label: string }) {
    const token = useAccessToken();
    const [filter, setFilter] = useState("");
    const [shown, setShown] = useState(PAGE);

    const files = useInfiniteQuery({
        queryKey: ["conversation-files", scope, id, token],
        queryFn: ({ pageParam }) => connectParityApi.listConversationFiles(scope, id, pageParam, token),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => last.nextBefore ?? undefined,
        enabled: Boolean(token)
    });

    const entries = useMemo<Entry[]>(() => {
        const out: Entry[] = [];
        const seen = new Set<string>();
        for (const page of files.data?.pages ?? []) {
            for (const m of page.messages) {
                const sharedBy = m.authorDisplayName || m.authorEmail;
                // The markers are parsed and validated exactly as in a message.
                for (const f of splitFileMarkers(m.markersHtml).files) {
                    const key = f.id.toLowerCase();
                    // Shared more than once: keep the newest.
                    if (seen.has(key)) continue;
                    seen.add(key);
                    out.push({ kind: "drive", key, file: f, sharedBy, sharedAt: m.sentAt });
                }
            }
            for (const l of page.legacy) {
                out.push({ kind: "legacy", key: `legacy-${l.id}`, item: l, sharedBy: l.authorDisplayName || l.authorEmail, sharedAt: l.uploadedAt });
            }
        }
        return out.sort((a, b) => new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime());
    }, [files.data]);

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => (e.kind === "drive" ? e.file.name : e.item.fileName).toLowerCase().includes(q));
    }, [entries, filter]);

    return (
        <div className="flex min-h-0 flex-1 flex-col" role="tabpanel" aria-label={`Files in ${label}`}>
            <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                    <input
                        type="search"
                        value={filter}
                        onChange={(e) => { setFilter(e.target.value); setShown(PAGE); }}
                        placeholder="Filter by name"
                        aria-label="Filter files by name"
                        className="h-8 w-full max-w-sm rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                </div>
                <span className="hidden text-xs text-muted-foreground sm:inline">
                    {entries.length} file{entries.length === 1 ? "" : "s"}{files.hasNextPage ? "+" : ""}
                </span>
                <button
                    type="button"
                    onClick={() => void files.refetch()}
                    disabled={files.isFetching}
                    aria-label="Refresh files"
                    title="Refresh"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                >
                    <RefreshCw className={files.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {files.isPending ? (
                    <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading files…
                    </p>
                ) : files.isError ? (
                    <p role="alert" className="p-4 text-sm text-destructive">Couldn't load the files. Try Refresh.</p>
                ) : entries.length === 0 && !files.hasNextPage ? (
                    <div className="flex h-48 flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                        <FolderOpen className="h-8 w-8 opacity-40" aria-hidden />
                        No files have been shared in {label} yet.
                    </div>
                ) : (
                    <>
                        <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,10rem)_6rem_5rem_auto] gap-x-3 border-b px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground md:grid">
                            <span>Name</span>
                            <span>Shared by</span>
                            <span>Date</span>
                            <span>Size</span>
                            <span className="w-[5.5rem]" aria-hidden />
                        </div>
                        {visible.length === 0 ? (
                            <p className="p-4 text-sm text-muted-foreground">No files match “{filter}”.</p>
                        ) : (
                            <ul role="list">
                                {visible.slice(0, shown).map((e) =>
                                    e.kind === "drive" ? (
                                        <DriveFileRow key={e.key} file={e.file} sharedBy={e.sharedBy} sharedAt={e.sharedAt} />
                                    ) : (
                                        <LegacyFileRow key={e.key} channelId={id} item={e.item} sharedBy={e.sharedBy} />
                                    )
                                )}
                            </ul>
                        )}
                        <div className="flex flex-wrap items-center justify-center gap-2 p-3">
                            {visible.length > shown ? (
                                <button type="button" onClick={() => setShown((n) => n + PAGE)} className="h-8 rounded-md border px-3 text-xs hover:bg-accent">
                                    Show more ({visible.length - shown})
                                </button>
                            ) : null}
                            {files.hasNextPage ? (
                                <button
                                    type="button"
                                    onClick={() => void files.fetchNextPage()}
                                    disabled={files.isFetchingNextPage}
                                    className="flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs hover:bg-accent disabled:opacity-50"
                                >
                                    {files.isFetchingNextPage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                    Look further back
                                </button>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/** Types the browser may show from a blob: never HTML or SVG, which could run script on our origin. */
function safeInlineType(contentType: string, name: string): string | null {
    const ct = (contentType || "").toLowerCase();
    switch (fileKind(name, ct)) {
        case "pdf": return "application/pdf";
        case "image": return ct.startsWith("image/") && ct !== "image/svg+xml" ? ct : null;
        case "video": return ct.startsWith("video/") ? ct : null;
        case "audio": return ct.startsWith("audio/") ? ct : null;
        default: return null;
    }
}

/** An attachment stored by Connect before files moved to Drive: download (and view, for safe types). */
function LegacyFileRow({ channelId, item, sharedBy }: {
    channelId: string;
    item: ConversationFilesPage["legacy"][number];
    sharedBy: string;
}) {
    const token = useAccessToken();
    const [busy, setBusy] = useState<"open" | "download" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const kind = fileKind(item.fileName, item.contentType);
    const inlineType = safeInlineType(item.contentType, item.fileName);
    const when = new Date(item.uploadedAt);

    const fetchBytes = async (): Promise<ArrayBuffer> => {
        const res = await fetch(connectParityApi.legacyAttachmentUrl(channelId, item.messageId, item.id), {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: "omit"
        });
        if (!res.ok) throw new Error(res.status === 404 ? "File unavailable" : `Download failed (${res.status})`);
        return res.arrayBuffer();
    };

    const run = async (what: "open" | "download") => {
        setBusy(what);
        setError(null);
        try {
            const bytes = await fetchBytes();
            if (what === "open" && inlineType) {
                // Typed by us, not the server, so the browser only renders it as that type.
                const url = URL.createObjectURL(new Blob([bytes], { type: inlineType }));
                window.open(url, "_blank", "noopener,noreferrer");
                window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
            } else {
                const url = URL.createObjectURL(new Blob([bytes], { type: "application/octet-stream" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = item.fileName || "attachment";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Download failed");
        } finally {
            setBusy(null);
        }
    };

    const action = "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50";
    return (
        <li className="border-b last:border-b-0">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2 hover:bg-accent/40 md:grid-cols-[minmax(0,1fr)_minmax(0,10rem)_6rem_5rem_auto]">
                <div className="flex min-w-0 items-center gap-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted/60">
                        <FileTypeIcon kind={kind} className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                        <span className="block truncate text-sm font-medium" title={item.fileName}>{item.fileName}</span>
                        <span className="block truncate text-[11px] text-muted-foreground md:hidden">
                            {[sharedBy, when.toLocaleDateString(), formatBytes(item.sizeBytes)].join(" · ")}
                        </span>
                        <span className="hidden text-[11px] text-muted-foreground md:block">Stored in Connect</span>
                    </span>
                </div>
                <span className="hidden truncate text-xs text-muted-foreground md:block" title={sharedBy}>{sharedBy}</span>
                <time className="hidden text-xs text-muted-foreground md:block" dateTime={item.uploadedAt} title={when.toLocaleString()}>
                    {when.toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                </time>
                <span className="hidden text-xs text-muted-foreground md:block">{formatBytes(item.sizeBytes)}</span>
                <div className="flex shrink-0 items-center gap-0.5">
                    {inlineType ? (
                        <button type="button" onClick={() => void run("open")} disabled={busy !== null} aria-label={`Open ${item.fileName}`} title="Open" className={action}>
                            {busy === "open" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                        </button>
                    ) : null}
                    <button type="button" onClick={() => void run("download")} disabled={busy !== null} aria-label={`Download ${item.fileName}`} title="Download" className={action}>
                        {busy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </button>
                </div>
            </div>
            {error ? <p role="alert" className="px-3 pb-1.5 text-[11px] text-destructive">{error}</p> : null}
        </li>
    );
}
